import React, { FC, useMemo } from 'react';
import { FinancesBlockProps } from './FinancesBlockProps';
import CustomNumberInput from '../../../common/CustomNumberInput';
import useTranslation from '../../../hooks/useTranslation';
import InputField from '@ingka/input-field';
import { Stack } from '@mui/material';
import Text from '@ingka/text';
import { useFormContext } from 'react-hook-form';
import { getTotalInvestmentSuffixLabel } from '../utils';

const FinancesBlock: FC<FinancesBlockProps> = ({
  nameApendix,
  currency,
  onChange,
  dataTestIdApendix,
  total,
  title,
  fullWidth,
  disabled,
  currencyRate,
  disablePropertyInvestmentValidation,
}) => {
  const t = useTranslation();
  const { watch } = useFormContext();
  const landCost = watch(`${nameApendix}.landCost`);
  const propertyInvestment = watch(`${nameApendix}.propertyInvestment`);

  const errorState = useMemo(() => {
    if (disablePropertyInvestmentValidation) return false;
    const property = parseInt(propertyInvestment) || 0;
    const land = parseInt(landCost) || 0;
    return property < land;
  }, [landCost, propertyInvestment, disablePropertyInvestmentValidation]);
  return (
    <Stack sx={fullWidth ? { width: '100%' } : { width: '100px', flex: 1 }}>
      <Text textStyle="Heading.S" tagName="h4">
        {title}
      </Text>
      <CustomNumberInput
        name={`${nameApendix}.landCost`}
        label={t(`projects.properties.LandCG01`)}
        currencySymbol={currency}
        customOnChange={onChange}
        disabled={disabled}
        dataTestID={`${dataTestIdApendix}-land`}
        currencyRate={currencyRate}
      />
      <CustomNumberInput
        name={`${nameApendix}.propertyInvestment`}
        label={t(`projects.properties.propertyInvestmentBuilding`)}
        currencySymbol={currency}
        customOnChange={onChange}
        disabled={disabled}
        dataTestID={`${dataTestIdApendix}-property-investment`}
        currencyRate={currencyRate}
        error={
          errorState
            ? t('validationMessages.propertyInvestmentShouldBeMoreThanLandCost')
            : null
        }
      />
      <CustomNumberInput
        name={`${nameApendix}.retailInvestment`}
        label={t(`projects.properties.retailInvestmentCG1214`)}
        currencySymbol={currency}
        customOnChange={onChange}
        disabled={disabled}
        dataTestID={`${dataTestIdApendix}-retail-investment`}
        currencyRate={currencyRate}
      />
      <CustomNumberInput
        name={`${nameApendix}.retailCost`}
        label={t(`projects.properties.retailCostCG1516`)}
        customOnChange={onChange}
        currencySymbol={currency}
        disabled={disabled}
        dataTestID={`${dataTestIdApendix}-retail-cost`}
        currencyRate={currencyRate}
      />
      <InputField
        suffixLabel={getTotalInvestmentSuffixLabel(
          currency,
          total,
          currencyRate,
        )}
        readOnly
        className={'read-only-input'}
        id={'totalProjectBudget'}
        label={t(`financialTotals.${nameApendix.split('.')[2]}`)}
        type={'text'}
        data-testid={`${dataTestIdApendix}-total-project-budget`}
        value={
          typeof total === 'number' || !isNaN(Number(total))
            ? Number(total).toLocaleString()
            : total
        }
      />
    </Stack>
  );
};

export default FinancesBlock;
